import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DatevKopplung, DatevKopplungEingabe } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { alsFeldwert, alsZahl, feldFehlerAus, KEINE_ZAHL, meldungAus, Meldung }
  from "./gemeinsam.js";
import "./datev.css";

/**
 * DER BLOCK „DATEV" in Verwaltung → Unternehmen (Spec, „Stammdaten je Unternehmen").
 *
 * Nur ADMIN — die ganze Verwaltung ist es. Er zeigt IMMER alle Felder, auch ohne
 * gespeicherte Zeile: Der Server antwortet dann mit den Vorgaben und
 * `eingerichtet: false`, und der erste Speichervorgang legt die Zeile an (Aufgabe 4).
 *
 * **Ein eigenes Formular mit eigenem Speichern-Knopf**, nicht Teil der Stammdatenmaske
 * darüber: Die Kopplung ist ein EIGENER Satz mit eigenem Weg (`PUT …/datev`), eigener
 * Prüfung und eigenen Feldfehlern. Sie an den Speichern-Knopf der Stammdaten zu hängen
 * hiesse, zwei Schreibwege hinter einem Knopf zu verstecken — und bei einem Fehlschlag
 * des zweiten stünde der erste schon in der Datenbank.
 *
 * **Bearbeitet wird im STRIKTEN Modalpanel** (Hausregel „Modal-Bearbeitung" — „Kopplung
 * pflegen" steht dort ausdrücklich): Das Detail LIEST die Felder, „Ändern" öffnet das
 * Formular. Dieselbe Bauart wie die zwölf Stammdatenfelder darüber in `unternehmen.tsx`.
 *
 * **Die Schalterzeile „Kopplung eingeschaltet" steht GANZ OBEN im Panel** und trägt den
 * Satz, der die wichtigste Auskunft dieses Blocks ist: „Ohne eingeschaltete Kopplung
 * laufen Rechnungen wie bisher; es entsteht kein Übergabeposten."
 *
 * Feldfehler kommen als 422 `DATEV_UNGUELTIG` mit `fields[]` und werden AN DAS FELD
 * geschrieben (Muster `KundeBeziehungen`); ein Feld, das diese Maske nicht zeigt, landet
 * im Meldungsband — unsichtbar darf kein Befund bleiben.
 *
 * Eigener CSS-Block `dtv` (views/verwaltung/datev.css) — dieselbe Wortwahl wie im Bereich
 * Schnittstellen → DATEV, aber ein EIGENES Stylesheet (Hausregel „CSS-Blocknamen je
 * Ansicht getrennt").
 */

export type DatevBlockClient = Pick<ApiClient, "getDatevKopplung" | "setDatevKopplung">;

export type DatevBlockProps = {
  client: DatevBlockClient;
  /** Das Unternehmen, dessen Kopplung hier steht. Ein Wechsel setzt ALLES zurück und lädt
   *  neu — derselbe Kontextwechsel wie im Bereich Schnittstellen → DATEV. */
  companyId: string;
};

/** Der Entwurf im Bearbeiten-Panel — Zahlenfelder als TEXT (ein Eingabefeld kennt kein
 *  `number | null`), zurückverwandelt beim Speichern über `alsZahl` (Muster
 *  `Nummernkreise.tsx`). */
type Entwurf = {
  consultantNumber: string; clientNumber: string;
  chartOfAccounts: "SKR03" | "SKR04";
  accountLength: string; fiscalYearStartMonth: string;
  revenueAccountStandard: string; revenueAccountReduced: string;
  revenueAccountExempt: string; revenueAccountIntraEu: string;
  taxKeyStandard: string; taxKeyReduced: string;
  taxKeyExempt: string; taxKeyIntraEu: string;
  debtorRangeFrom: string; debtorRangeTo: string;
  active: boolean;
};

function entwurfAus(k: DatevKopplung): Entwurf {
  return {
    consultantNumber: alsFeldwert(k.consultantNumber),
    clientNumber: alsFeldwert(k.clientNumber),
    chartOfAccounts: k.chartOfAccounts,
    accountLength: String(k.accountLength),
    fiscalYearStartMonth: String(k.fiscalYearStartMonth),
    revenueAccountStandard: k.revenueAccountStandard,
    revenueAccountReduced: k.revenueAccountReduced,
    revenueAccountExempt: k.revenueAccountExempt,
    revenueAccountIntraEu: k.revenueAccountIntraEu,
    taxKeyStandard: String(k.taxKeyStandard),
    taxKeyReduced: String(k.taxKeyReduced),
    taxKeyExempt: String(k.taxKeyExempt),
    taxKeyIntraEu: String(k.taxKeyIntraEu),
    debtorRangeFrom: String(k.debtorRangeFrom),
    debtorRangeTo: String(k.debtorRangeTo),
    active: k.active,
  };
}

/** Nur die Zahlenfelder — für `alsZahl` und die 422-Feldnamen. Die drei Textfelder
 *  (`chartOfAccounts`, vier `revenueAccount*`) tippt man unverändert durch. */
const ZAHLENFELDER = [
  "consultantNumber", "clientNumber", "accountLength", "fiscalYearStartMonth",
  "taxKeyStandard", "taxKeyReduced", "taxKeyExempt", "taxKeyIntraEu",
  "debtorRangeFrom", "debtorRangeTo",
] as const;
type Zahlenfeld = (typeof ZAHLENFELDER)[number];

/** Alle Feldnamen, die eine 422 `DATEV_UNGUELTIG` nennen kann UND die diese Maske zeigt. */
type FeldName = Zahlenfeld | "chartOfAccounts" | "revenueAccountStandard"
  | "revenueAccountReduced" | "revenueAccountExempt" | "revenueAccountIntraEu";

const FELD_LABEL: Record<FeldName, string> = {
  consultantNumber: "Beraternummer", clientNumber: "Mandantennummer",
  chartOfAccounts: "Kontenrahmen", accountLength: "Sachkontenlänge",
  fiscalYearStartMonth: "Wirtschaftsjahr-Beginn",
  revenueAccountStandard: "Erlöskonto Standard",
  revenueAccountReduced: "Erlöskonto ermässigt",
  revenueAccountExempt: "Erlöskonto steuerfrei",
  revenueAccountIntraEu: "Erlöskonto innergemeinschaftlich",
  taxKeyStandard: "BU-Schlüssel Standard", taxKeyReduced: "BU-Schlüssel ermässigt",
  taxKeyExempt: "BU-Schlüssel steuerfrei", taxKeyIntraEu: "BU-Schlüssel innergemeinschaftlich",
  debtorRangeFrom: "Debitorenkreis von", debtorRangeTo: "Debitorenkreis bis",
};

export function DatevBlock(p: DatevBlockProps): ReactElement {
  const [kopplung, setKopplung] = useState<DatevKopplung | null | undefined>(undefined);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<FeldName, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  const lauf = useRef(0);
  const companyId = p.companyId;
  const getDatevKopplung = p.client.getDatevKopplung;

  /* KEIN ZUSTAND ÜBERLEBT DEN UNTERNEHMENSWECHSEL (Hausregel). */
  useEffect(() => {
    const meiner = (lauf.current += 1);
    setKopplung(undefined);
    setLadeFehler(null);
    setEntwurf(null);
    setFeldFehler({});
    setMeldung(null);
    setErfolg(false);
    // Als ASYNC FUNKTION und nicht als rohe Promise-Kette: Ein synchroner Fehlschlag des
    // Aufrufs selbst (etwa im Test ein fehlender `vi.fn`) landet damit ebenfalls im
    // `catch` statt als unbehandelte Ausnahme den ganzen Effekt zu sprengen.
    (async () => {
      try {
        const k = await getDatevKopplung(companyId);
        if (meiner === lauf.current) setKopplung(k);
      } catch (e) {
        if (meiner !== lauf.current) return;
        setKopplung(null);
        setLadeFehler(meldungAus(e, "Die DATEV-Kopplung liess sich nicht laden."));
      }
    })();
    return () => { lauf.current += 1; };
  }, [companyId, getDatevKopplung]);

  const geaendert = entwurf !== null && kopplung !== null && kopplung !== undefined
    && JSON.stringify(entwurf) !== JSON.stringify(entwurfAus(kopplung));
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    if (kopplung == null) return;
    setEntwurf(entwurfAus(kopplung));
    setFeldFehler({});
    setMeldung(null);
    setErfolg(false);
  }

  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(geaendert)) return;
    setEntwurf(null);
    setFeldFehler({});
  }

  function feldWert(feld: keyof Entwurf, wert: string): void {
    setEntwurf((e) => (e === null ? e : { ...e, [feld]: wert }));
  }

  async function speichern(): Promise<void> {
    const e = entwurf;
    if (e === null || speichert) return;
    const meiner = lauf.current;

    const zahlen: Partial<Record<Zahlenfeld, number | null>> = {};
    const ungueltig: FeldName[] = [];
    for (const feld of ZAHLENFELDER) {
      const roh = alsZahl(e[feld]);
      if (roh === KEINE_ZAHL) { ungueltig.push(feld); continue; }
      zahlen[feld] = roh;
    }
    if (ungueltig.length > 0) {
      const neu: Partial<Record<FeldName, string>> = {};
      for (const f of ungueltig) neu[f] = `${FELD_LABEL[f]} ist keine Zahl.`;
      setFeldFehler(neu);
      return;
    }
    // Berater-/Mandantennummer dürfen leer sein (NULL), alle übrigen Zahlenfelder sind
    // Pflicht — der Server weist ein fehlendes Pflichtfeld ohnehin mit 422 ab, aber ein
    // leeres Sachkonten-/BU-Feld hier abzuschicken behauptete „0", was niemand gemeint hat.
    const PFLICHTFELDER = ["accountLength", "fiscalYearStartMonth", "taxKeyStandard",
      "taxKeyReduced", "taxKeyExempt", "taxKeyIntraEu", "debtorRangeFrom",
      "debtorRangeTo"] as const;
    const pflichtLeer = PFLICHTFELDER.filter((f) => zahlen[f] === null);
    if (pflichtLeer.length > 0) {
      const neu: Partial<Record<FeldName, string>> = {};
      for (const f of pflichtLeer) neu[f] = `${FELD_LABEL[f]} darf nicht leer sein.`;
      setFeldFehler(neu);
      return;
    }

    setSpeichert(true);
    setFeldFehler({});
    setMeldung(null);
    try {
      const eingabe: DatevKopplungEingabe = {
        consultantNumber: zahlen.consultantNumber ?? null,
        clientNumber: zahlen.clientNumber ?? null,
        chartOfAccounts: e.chartOfAccounts,
        accountLength: zahlen.accountLength!,
        fiscalYearStartMonth: zahlen.fiscalYearStartMonth!,
        revenueAccountStandard: e.revenueAccountStandard,
        revenueAccountReduced: e.revenueAccountReduced,
        revenueAccountExempt: e.revenueAccountExempt,
        revenueAccountIntraEu: e.revenueAccountIntraEu,
        taxKeyStandard: zahlen.taxKeyStandard!,
        taxKeyReduced: zahlen.taxKeyReduced!,
        taxKeyExempt: zahlen.taxKeyExempt!,
        taxKeyIntraEu: zahlen.taxKeyIntraEu!,
        debtorRangeFrom: zahlen.debtorRangeFrom!,
        debtorRangeTo: zahlen.debtorRangeTo!,
        active: e.active,
      };
      const neu = await p.client.setDatevKopplung(companyId, eingabe);
      if (meiner !== lauf.current) return;
      setKopplung(neu);
      setEntwurf(null);
      setErfolg(true);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      if (fehler instanceof ApiError && fehler.fields !== undefined
        && fehler.fields.length > 0) {
        const { felder, unzuordenbar } = feldFehlerAus(fehler,
          [...ZAHLENFELDER, "chartOfAccounts", "revenueAccountStandard",
            "revenueAccountReduced", "revenueAccountExempt", "revenueAccountIntraEu"]);
        setFeldFehler(felder);
        if (unzuordenbar || Object.keys(felder).length === 0) setMeldung(fehler.message);
      } else if (fehler instanceof ApiError) {
        setMeldung(fehler.message);
      } else {
        setMeldung("Unerwarteter Fehler beim Speichern der Kopplung.");
      }
    } finally {
      setSpeichert(false);
    }
  }

  function zeile(feld: FeldName, wert: string): ReactElement {
    return (
      <p className="dtv__eintrag-zusatz">
        {FELD_LABEL[feld]}: {wert.trim() === "" ? "nicht gepflegt" : wert}
      </p>
    );
  }

  return (
    <section className="dtv" aria-label="DATEV">
      <h3 className="dtv__untertitel">DATEV</h3>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {erfolg && entwurf === null && (
        <p className="dtv__erfolg" role="status">Gespeichert.</p>
      )}
      {meldung !== null && entwurf === null && <Meldung text={meldung} />}

      {kopplung === undefined ? (
        <p className="dtv__hinweis">Lädt …</p>
      ) : kopplung === null ? null : (
        <>
          <p className="dtv__eintrag-zusatz">
            Kopplung: {kopplung.active ? "eingeschaltet" : "ausgeschaltet"}
            {!kopplung.eingerichtet && " (noch nicht gespeichert)"}
          </p>
          {zeile("consultantNumber", alsFeldwert(kopplung.consultantNumber))}
          {zeile("clientNumber", alsFeldwert(kopplung.clientNumber))}
          {zeile("chartOfAccounts", kopplung.chartOfAccounts)}
          {zeile("accountLength", String(kopplung.accountLength))}
          {zeile("fiscalYearStartMonth", String(kopplung.fiscalYearStartMonth))}
          {zeile("revenueAccountStandard", kopplung.revenueAccountStandard)}
          {zeile("revenueAccountReduced", kopplung.revenueAccountReduced)}
          {zeile("revenueAccountExempt", kopplung.revenueAccountExempt)}
          {zeile("revenueAccountIntraEu", kopplung.revenueAccountIntraEu)}
          {zeile("taxKeyStandard", String(kopplung.taxKeyStandard))}
          {zeile("taxKeyReduced", String(kopplung.taxKeyReduced))}
          {zeile("taxKeyExempt", String(kopplung.taxKeyExempt))}
          {zeile("taxKeyIntraEu", String(kopplung.taxKeyIntraEu))}
          {zeile("debtorRangeFrom", String(kopplung.debtorRangeFrom))}
          {zeile("debtorRangeTo", String(kopplung.debtorRangeTo))}

          <div className="dtv__aktionen">
            {/* Der zugängliche Name endet wie an jedem anderen Änderungsknopf dieser
                Verwaltung auf „ändern" und nennt davor das Objekt (Muster
                `oeffneAenderungspanel`, e2e/support/verwaltung.ts) — ohne das eigene
                `aria-label` läse ein Bildschirmleser hier nur „Ändern" und wüsste nicht,
                wovon. */}
            <button type="button" className="dtv__knopf" disabled={entwurf !== null}
              aria-label="DATEV-Kopplung ändern" onClick={oeffnen}>
              Ändern
            </button>
          </div>
        </>
      )}

      {entwurf !== null && (
        <Modal titel="DATEV-Kopplung" breite="breit" onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert}>
          <div className="dtv__dialoginhalt">
            {meldung !== null && <Meldung text={meldung} />}

            {/* DIE SCHALTERZEILE STEHT GANZ OBEN (Kopfkommentar). */}
            <label className="dtv__schalter">
              <input type="checkbox" checked={entwurf.active}
                onChange={(ev) => setEntwurf((e) =>
                  (e === null ? e : { ...e, active: ev.target.checked }))} />
              <span>Kopplung eingeschaltet</span>
            </label>
            <p className="dtv__hinweis">
              Ohne eingeschaltete Kopplung laufen Rechnungen wie bisher; es entsteht kein
              Übergabeposten.
            </p>

            {(["consultantNumber", "clientNumber"] as const).map((feld) => (
              <div key={feld} className={feldFehler[feld] !== undefined
                ? "ki-feld ki-feld--fehler" : "ki-feld"}>
                <label htmlFor={`dtv-${feld}`} className="ki-feld__label">
                  {FELD_LABEL[feld]}
                </label>
                <input id={`dtv-${feld}`} className="ki-feld__eingabe" type="text"
                  inputMode="numeric" value={entwurf[feld]}
                  onChange={(ev) => feldWert(feld, ev.target.value)} />
                {feldFehler[feld] !== undefined && (
                  <span className="ki-feld__fehler">{feldFehler[feld]}</span>
                )}
              </div>
            ))}

            <div className={feldFehler.chartOfAccounts !== undefined
              ? "ki-feld ki-feld--fehler" : "ki-feld"}>
              <label htmlFor="dtv-chartOfAccounts" className="ki-feld__label">
                Kontenrahmen
              </label>
              <select id="dtv-chartOfAccounts" className="ki-feld__eingabe"
                value={entwurf.chartOfAccounts}
                onChange={(ev) => setEntwurf((e) => (e === null ? e
                  : { ...e, chartOfAccounts: ev.target.value as "SKR03" | "SKR04" }))}>
                <option value="SKR03">SKR03</option>
                <option value="SKR04">SKR04</option>
              </select>
            </div>

            {(["accountLength", "fiscalYearStartMonth"] as const).map((feld) => (
              <div key={feld} className={feldFehler[feld] !== undefined
                ? "ki-feld ki-feld--fehler" : "ki-feld"}>
                <label htmlFor={`dtv-${feld}`} className="ki-feld__label">
                  {FELD_LABEL[feld]}
                </label>
                <input id={`dtv-${feld}`} className="ki-feld__eingabe" type="text"
                  inputMode="numeric" value={entwurf[feld]}
                  onChange={(ev) => feldWert(feld, ev.target.value)} />
                {feldFehler[feld] !== undefined && (
                  <span className="ki-feld__fehler">{feldFehler[feld]}</span>
                )}
              </div>
            ))}

            {(["revenueAccountStandard", "revenueAccountReduced", "revenueAccountExempt",
              "revenueAccountIntraEu"] as const).map((feld) => (
              <div key={feld} className={feldFehler[feld] !== undefined
                ? "ki-feld ki-feld--fehler" : "ki-feld"}>
                <label htmlFor={`dtv-${feld}`} className="ki-feld__label">
                  {FELD_LABEL[feld]}
                </label>
                <input id={`dtv-${feld}`} className="ki-feld__eingabe" type="text"
                  value={entwurf[feld]} onChange={(ev) => feldWert(feld, ev.target.value)} />
                {feldFehler[feld] !== undefined && (
                  <span className="ki-feld__fehler">{feldFehler[feld]}</span>
                )}
              </div>
            ))}

            {(["taxKeyStandard", "taxKeyReduced", "taxKeyExempt", "taxKeyIntraEu"] as const)
              .map((feld) => (
                <div key={feld} className={feldFehler[feld] !== undefined
                  ? "ki-feld ki-feld--fehler" : "ki-feld"}>
                  <label htmlFor={`dtv-${feld}`} className="ki-feld__label">
                    {FELD_LABEL[feld]}
                  </label>
                  <input id={`dtv-${feld}`} className="ki-feld__eingabe" type="text"
                    inputMode="numeric" value={entwurf[feld]}
                    onChange={(ev) => feldWert(feld, ev.target.value)} />
                  {feldFehler[feld] !== undefined && (
                    <span className="ki-feld__fehler">{feldFehler[feld]}</span>
                  )}
                </div>
              ))}

            {/* Debitorenkreis — die Vorgaben sind ÄNDERBAR (Global Constraints): ohne
                Bereichsprüfung hier, geprüft wird auf dem Server. */}
            {(["debtorRangeFrom", "debtorRangeTo"] as const).map((feld) => (
              <div key={feld} className={feldFehler[feld] !== undefined
                ? "ki-feld ki-feld--fehler" : "ki-feld"}>
                <label htmlFor={`dtv-${feld}`} className="ki-feld__label">
                  {FELD_LABEL[feld]}
                </label>
                <input id={`dtv-${feld}`} className="ki-feld__eingabe" type="text"
                  inputMode="numeric" value={entwurf[feld]}
                  onChange={(ev) => feldWert(feld, ev.target.value)} />
                {feldFehler[feld] !== undefined && (
                  <span className="ki-feld__fehler">{feldFehler[feld]}</span>
                )}
              </div>
            ))}

            {/* Fixrunde 2, I2: Der Produktivbestand aus TOPIX (Migration 028, Teil 7)
                trägt SIEBENSTELLIGE Debitorennummern — ein Kreis, der das nicht
                abdeckt, lässt offene Posten leer bleiben und bestehende Nummern nicht
                ändern (`istImKreis`, `setzeDebitorennummer`). Der Hinweis steht HIER,
                direkt unter den beiden Feldern, statt nur in der Doku, weil er genau in
                dem Moment gebraucht wird, in dem jemand den Kreis einträgt. */}
            <p className="dtv__hinweis">
              Bestand aus TOPIX ist siebenstellig: Sachkontenlänge 6, Debitorenkreis
              1000000–6999999 eintragen, sonst bleiben offene Posten leer und bestehende
              Nummern lassen sich nicht ändern.
            </p>
          </div>

          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
